import React, { useEffect, useMemo, useState } from 'react';
import { HiChevronDown, HiExclamationCircle, HiTrash } from 'react-icons/hi';
import './DependencyCard.css';
import { FaEdit } from 'react-icons/fa';
import ConfirmationDialog from '../../../pages/DashboardNew/components/ConfirmationDialog';
import { getEntityTypeDisplay } from '../../../utils/entityDisplayUtils';
import { DependencyCardData } from './DependencyCard.def';
import TruncatedText from '../../../components/common/TruncatedText';
import TooltipWrapper from '../../atoms/TooltipWrapper';
interface DependencyCardProps {
  dependency: DependencyCardData;
  onRemove: () => void;
  onEdit?: (data: DependencyCardData) => void;
  entityType?: string;
  onExpanded?: (open: boolean) => void;
  expanded?: boolean;
  onTitleClick?: () => void;
  EditorComponent?: React.ComponentType<{
    dependency: { selectedSource: string; selectedDependencies: string[] };
    onCancel: () => void;
  }>;
}

const DependencyCard: React.FC<DependencyCardProps> = ({
  dependency,
  onRemove,
  onEdit,
  entityType = "Work Item",
  onExpanded,
  expanded,
  EditorComponent
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [deleteConf, setDeleteConf] = useState<{
    show: boolean;
    position?: { top: number; left: number };
  }>({ show: false });

  const [isEditing, setIsEditing] = useState(false);


  useEffect(() => {
    setIsExpanded(expanded);
  }, [expanded]);

  const handleRemoveClick = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    
    setDeleteConf({
      show: true,
      position: {
        top: rect.top,
        left: rect.left - 215
      }
    });
  };


  const handleEdit = () => {
    setIsEditing((pv)=>!pv);
    setIsExpanded(true);
    onEdit?.(dependency);
  }
  const entityInfo = getEntityTypeDisplay(
      dependency?.entity_type ?? "strategy_map",
      dependency.type,
    );
  
    const IconComponent = entityInfo.icon;
  
  const isHavingInvalidDependency = useMemo(()=>dependency.dependency.some((dep) => dep.invalid_dependency),[dependency.dependency]);

  return (
    <div
      className={`dependency-card ${isExpanded ? "dependency-card--expanded" : ""} ${isHavingInvalidDependency ? "dependency-card--invalid" : ""}`}
    >
      {/* Header */}
      <div
        className="dependency-card__header group"
        onClick={() => {
          onExpanded?.(isExpanded);

          // setIsExpanded(!isExpanded);
        }}
      >
        <span className="dependency-card__bullet">
          <IconComponent
            className={`${entityInfo.iconClass} w-[20px] h-[20px]`}
          />
        </span>
        <span
          className="dependency-card__title hover:text-primary-light dark:hover:text-primary-dark cursor-pointer"
          title={dependency.name}
         
        >
          {dependency.name}
        </span>
        <button
          className="dependency-card__expand-btn"
          aria-label="Expand item"
        >
          <span
            className={`dependency-card__expand-icon ${isExpanded ? "dependency-card__expand-icon--expanded" : ""}`}
          >
            <HiChevronDown size={18} />
          </span>
        </button>
      </div>

      {/* Sub-header status and actions */}
      <div className="dependency-card__meta">
        <img
          src={"/configuration%20green.svg"}
          alt="Dependency"
          className="w-4 h-4 mr-2 inline-block"
        />
        <span className="dependency-card__badge">
          {dependency.dependency.length}{" "}
          {dependency.dependency.length === 1 ? "Dependency" : "Dependencies"}
        </span>

        <div className="dependency-card__actions">
          <button
            className="dependency-card__edit-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit();
            }}
            title="Edit mapping"
          >
            <FaEdit className="dependency-card__edit-icon" size={14} />
          </button>
          <button
            className="dependency-card__delete-btn"
            onClick={(e) => {
              // e.stopPropagation();
              handleRemoveClick(e);
            }}
            title="Delete mapping"
          >
            <HiTrash className="dependency-card__delete-icon" size={14} />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="dependency-card__content">
          <div className="dependency-card__label">Depends on</div>
          {dependency.dependency.map((dep) => {
            const entityInfo = getEntityTypeDisplay(
              dep?.entity_type ?? "strategy_map",
              dep.type,
            );

            const DepChildIconComponent = entityInfo.icon;
            const isInvalid = dep.invalid_dependency;
            return (
              <div
                key={dep.id}
                className={`dependency-card__item ${isInvalid ? "dependency-card__item--invalid" : ""}`}
              >
                <span className="dependency-card__item-badge" title={dep.name}>
                  {dep.name}
                </span>
                <span className="dependency-card__item-type ml-auto">
                  <DepChildIconComponent
                    className={`${entityInfo.iconClass} w-[14px] h-[14px]`}
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}

      {isEditing && EditorComponent && isExpanded && (
        <div className="animate-fade-in">
          <EditorComponent
            onCancel={() => setIsEditing(false)}
            dependency={{
              selectedSource: dependency.id,
              selectedDependencies: dependency.dependency.map((d) => d.id),
            }}
          />
        </div>
      )}

      <ConfirmationDialog
        isOpen={deleteConf.show}
        type="single"
        position={deleteConf.position}
        dependencyName={dependency.name}
        entityLabel="dependency"
        onConfirm={() => {
          onRemove();
          setDeleteConf({ show: false });
        }}
        onCancel={() => setDeleteConf({ show: false })}
      />

      {isHavingInvalidDependency && (
        <TooltipWrapper message='Some of the items in this dependency are invalid. Please resolve the issue before proceeding.'>
          <div className="dependency-card__invalid">

          <span className="dependency-card__invalid-icon">
            <HiExclamationCircle size={16} />
          </span>
          <span className="dependency-card__invalid-text">
            Action Required
          </span>
          </div>
        </TooltipWrapper>
      )}
    </div>
  );
};

export default DependencyCard;
